"use client";
import React from "react";
import {
  isNumber,
  get,
  isFunction,
  isEqual,
  isEmpty,
  assign,
  isString,
} from "utils/lodash";

interface LoaderFunc {
  (opts: { page: number; limit: number; [key: string]: any }): Promise<any>;
}
interface TypeInitialParams {
  type?: string; // infinity | ""
  page?: number;
  limit?: number;
  [key: string]: any;
}
interface TypeConfig {
  initialParams?: TypeInitialParams;
  loader?: LoaderFunc;
  onSearch?: (key: string, value: any) => void;
  onChangeFilter?: (filters: object, sorter: object, columns?: any) => void;
}
interface TypeUsePaginationReturn {
  items: any[];
  page: number;
  limit: number;
  total: number;
  isLoading: boolean;
  errors?: any;
  hasPreviousPage: boolean;
  hasNextPage: boolean;
  onLoadPage: (page: number) => void;
  onPreviousPage: () => void;
  onNextPage: () => void;
  onChangePage: (page: number) => void;
  onSearch: (key: string, value: any) => void; // custom action
  onReload: () => void;
  onRefresh: () => void;
  submit: (payload: { [key: string]: any }) => void;
}
interface TypeUsePagination {
  (config?: TypeConfig): TypeUsePaginationReturn;
}
/**
 *
 * @param { initialParams: <PlainObject>, loader: <AsyncFunction> } config
 * @returns { items, page, total, isLoading, errors, hasPreviousPage, hasNextPage, onLoadPage, onPreviousPage, onNextPage, onChangePage } PaginationModel
 */

const usePagination: TypeUsePagination = (config: TypeConfig = {}) => {
  const ref: any = React.useRef({});
  const [limit, $limit] = React.useState(
    get(config, "initialParams.limit", 10)
  );

  const type = get(config, "initialParams.type", "");
  // initialItems/initialTotal: dữ liệu trang 1 đã lấy ở server → không cần gọi lại khi mount
  const [items, $items] = React.useState<any[]>(
    get(config, "initialParams.initialItems") || []
  );
  const [page, $page] = React.useState(get(config, "initialParams.page", 1));
  const [total, $total] = React.useState(
    get(config, "initialParams.initialTotal", 0)
  );
  const [isLoading, $isLoading] = React.useState(
    get(config, "initialParams.isLoading") ??
      !get(config, "initialParams.initialItems")
  );
  const [errors, $errors] = React.useState(null);
  assign(ref.current, {
    items,
    $items,
    page,
    $page,
    limit,
    $limit,
    total,
    $total,
    isLoading,
    $isLoading,
    errors,
    $errors,
  });
  const dataLoader = async (opts?: any) => {
    // Request-id chống race: đổi danh mục/trang nhanh có thể bắn 2 lượt gọi
    // gần nhau, lượt cũ trả về SAU lượt mới (mạng chậm/không đều) → nếu không
    // kiểm, state bị response cũ ghi đè lên response mới (grid hiện kết quả
    // cũ trong khi chip đã chọn mới). Không dùng AbortController vì loader là
    // hàm bất kỳ do component truyền vào (fetchOfferPage/fetchPackagePage…),
    // không nhận AbortSignal — đổi signature sẽ lan ra nhiều service. Đánh số
    // thứ tự lượt gọi, chỉ commit state nếu vẫn là lượt mới nhất khi resolve.
    const mySeq = (ref.current.requestSeq = (ref.current.requestSeq || 0) + 1);
    try {
      if (!isLoading) {
        $isLoading(true);
      }
      const { loader } = config;
      const { page: oPage, limit: oLimit, reload: oReload, ...opt } = opts;

      if (isFunction(loader)) {
        const reload = oReload ?? false;
        const page = oPage ?? ref.current.page;
        const limit = oLimit ?? ref.current.limit;
        const payload = {
          page,
          limit,
          ...opt,
        };
        const res = await loader(payload);
        // Lượt gọi này đã bị 1 lượt mới hơn thay thế (người dùng đổi danh mục/trang
        // trong lúc đang đợi) → bỏ kết quả trễ, không ghi đè state của lượt mới.
        if (ref.current.requestSeq !== mySeq) return;
        const total = get(res, "total", 0);
        if (!isEqual(ref.current.total, total)) {
          ref.current.$total(total);
        }
        if (!isEqual(ref.current.page, page)) {
          ref.current.$page(page);
        }
        if (!isEqual(ref.current.limit, limit)) {
          ref.current.$limit(limit);
        }
        const newErrors = get(res, "errors", null);
        if (!isEmpty(newErrors)) {
          ref.current.$errors(newErrors);
        }
        const newItems = get(res, "data", []);

        if (type === "infinity" && !reload) {
          ref.current.$items([...ref.current.items, ...newItems]);
        } else {
          ref.current.$items(newItems);
        }
        $isLoading(false);
      }
    } catch (errors: any) {
      if (ref.current.requestSeq !== mySeq) return;
      $errors(errors);
      console.log({ errors });
      $isLoading(false);
    }
  };
  // Số trang = làm tròn LÊN. Công thức cũ round(x + 0.5) sai khi chia hết:
  // 20/10 = 2 → round(2.5) = 3 trang → "Xem thêm" hiện thêm 1 lần rồi trả rỗng.
  const hasNextPage = ref.current.page < Math.ceil(total / ref.current.limit);
  const hasPreviousPage = ref.current.page > 1;
  const onLoadPage = (page: number) => {
    const limit = get(config, "limit") || ref.current.limit;
    dataLoader({ page, limit });
  };
  const onNextPage = () => {
    if (hasNextPage) {
      onLoadPage(ref.current.page + 1);
    }
  };
  const onPreviousPage = () => {
    if (hasPreviousPage) {
      onLoadPage(ref.current.page - 1);
    }
  };
  const onChangePage = (page: number) => {
    if (page > ref.current.page) {
      onNextPage();
    } else if (page < ref.current.page) {
      onPreviousPage();
    }
  };

  const onSearch = (key: string, value: any) => {
    dataLoader({ [key]: value, page: 1, reload: true });
  };
  const onReload = () => {
    ref.current.$page(1);
    const limit = get(config, "limit") || ref.current.limit;
    dataLoader({ page: 1, limit, reload: true });
  };
  const onRefresh = () => {
    dataLoader();
  };
  const submit = (payload: { [key: string]: any }) => {
    // payload.page (nếu có) được ưu tiên — dùng cho lần tải đầu đọc ?page= từ URL.
    dataLoader({ page: 1, ...payload, reload: true });
  };
  return {
    items,
    page,
    limit,
    total,
    isLoading,
    errors,
    hasPreviousPage,
    hasNextPage,
    onLoadPage,
    onPreviousPage,
    onNextPage,
    onChangePage,
    onSearch,
    onReload,
    onRefresh,
    submit,
  };
};

export default usePagination;
